import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowUpRight, ArrowDownRight, Users, Globe, Receipt, Building2, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAppContext } from '../contexts/AppContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { StatCard } from '../components/ui/StatCard';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { toast } from 'sonner';
export const Overview: React.FC = () => {
  const {
    state,
    dispatch
  } = useAppContext();
  const navigate = useNavigate();
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState('');
  const {
    user,
    transactions,
    campaigns,
    transfers,
    properties,
    maintenanceRequests
  } = state;
  // Calculate stats
  const activeCampaigns = campaigns.filter(c => c.status === 'Active').length;
  const totalSent = transfers.reduce((sum, t) => sum + t.ngnAmount, 0);
  const totalBills = transactions.filter(t => t.type === 'Bills').reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const activeRequests = maintenanceRequests.filter(r => r.status !== 'Completed').length;
  const recentTransactions = transactions.slice(0, 5);
  // Mock chart data
  const chartData = [{
    name: 'Jan',
    Crowdfunding: 4000,
    Diaspora: 24000,
    Bills: 2400,
    Facility: 0
  }, {
    name: 'Feb',
    Crowdfunding: 3000,
    Diaspora: 13980,
    Bills: 2210,
    Facility: 350000
  }, {
    name: 'Mar',
    Crowdfunding: 2000,
    Diaspora: 98000,
    Bills: 2290,
    Facility: 0
  }, {
    name: 'Apr',
    Crowdfunding: 2780,
    Diaspora: 39080,
    Bills: 2000,
    Facility: 0
  }, {
    name: 'May',
    Crowdfunding: 1890,
    Diaspora: 48000,
    Bills: 2181,
    Facility: 0
  }, {
    name: 'Jun',
    Crowdfunding: 2390,
    Diaspora: 38000,
    Bills: 2500,
    Facility: 350000
  }];
  const handleAddFunds = (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(fundAmount);
    if (amount < 1000) {
      toast.error('Minimum amount is ₦1,000');
      return;
    }
    dispatch({
      type: 'ADD_FUNDS',
      payload: amount
    });
    dispatch({
      type: 'ADD_TRANSACTION',
      payload: {
        id: `tx_${Date.now()}`,
        type: 'Wallet',
        title: 'Card Funding',
        amount: amount,
        status: 'Completed',
        date: new Date().toISOString(),
        reference: `FND-${Date.now().toString().slice(-6)}`
      }
    });
    toast.success(`Successfully added ${formatCurrency(amount)} to your wallet`);
    setIsAddFundsOpen(false);
    setFundAmount('');
  };
  return <div className="space-y-6" data-id="element-782">
      {/* Top Section: Wallet & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-783">
        {/* Wallet Card */}
        <div className="lg:col-span-2 bg-brand-primary-dark rounded-3xl p-8 text-white relative overflow-hidden shadow-lg shadow-brand-primary-dark/20" data-id="element-784">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-785" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-primary/20 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4" data-id="element-786" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6" data-id="element-787">
            <div data-id="element-788">
              <p className="text-brand-primary-light font-medium mb-2" data-id="element-789">
                Total Balance
              </p>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight" data-id="element-790">
                {formatCurrency(user?.walletBalance || 0)}
              </h2>
            </div>
            <div className="flex gap-3" data-id="element-791">
              <button onClick={() => setIsAddFundsOpen(true)} className="bg-brand-accent hover:bg-orange-400 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2" data-id="element-792">
                <Plus size={20} data-id="element-793" /> Add Funds
              </button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex flex-col justify-center" data-id="element-794">
          <h3 className="text-sm font-semibold text-brand-text-light uppercase tracking-wider mb-4" data-id="element-795">
            Quick Actions
          </h3>
          <div className="grid grid-cols-2 gap-3" data-id="element-796">
            <button onClick={() => navigate('/dashboard/diaspora')} className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-gray-50 hover:bg-brand-primary/5 hover:text-brand-primary transition-colors text-brand-text-dark" data-id="element-797">
              <ArrowUpRight size={24} data-id="element-798" />
              <span className="text-sm font-medium" data-id="element-799">Send</span>
            </button>
            <button onClick={() => navigate('/dashboard/bills')} className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-gray-50 hover:bg-brand-primary/5 hover:text-brand-primary transition-colors text-brand-text-dark" data-id="element-800">
              <Receipt size={24} data-id="element-801" />
              <span className="text-sm font-medium" data-id="element-802">Pay Bills</span>
            </button>
            <button onClick={() => navigate('/dashboard/crowdfunding')} className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-gray-50 hover:bg-brand-primary/5 hover:text-brand-primary transition-colors text-brand-text-dark" data-id="element-803">
              <Users size={24} data-id="element-804" />
              <span className="text-sm font-medium" data-id="element-805">Fundraise</span>
            </button>
            <button onClick={() => navigate('/dashboard/facility')} className="flex flex-col items-center justify-center gap-2 p-4 rounded-2xl bg-gray-50 hover:bg-brand-primary/5 hover:text-brand-primary transition-colors text-brand-text-dark" data-id="element-806">
              <Building2 size={24} data-id="element-807" />
              <span className="text-sm font-medium" data-id="element-808">Rent</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-809">
        <StatCard title="Active Campaigns" value={activeCampaigns} icon={Users} colorClass="text-blue-600" data-id="element-810" />
        <StatCard title="Total Sent (Diaspora)" value={formatCurrency(totalSent)} icon={Globe} colorClass="text-brand-primary" data-id="element-811" />
        <StatCard title="Bills Paid" value={formatCurrency(totalBills)} icon={Receipt} colorClass="text-purple-600" data-id="element-812" />
        <StatCard title="Pending Maintenance" value={activeRequests} icon={AlertCircle} colorClass="text-brand-accent" data-id="element-813" />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-814">
        {/* Chart */}
        <div className="lg:col-span-2 bg-white rounded-3xl p-6 border border-gray-100 shadow-sm" data-id="element-815">
          <h3 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-816">
            Spending Overview
          </h3>
          <div className="h-72 w-full" data-id="element-817">
            <ResponsiveContainer width="100%" height="100%" data-id="element-818">
              <BarChart data={chartData} margin={{
              top: 0,
              right: 0,
              left: -20,
              bottom: 0
            }} data-id="element-819">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" data-id="element-820" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} dy={10} data-id="element-821" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#6b7280',
                fontSize: 12
              }} tickFormatter={value => `₦${value / 1000}k`} data-id="element-822" />
                <Tooltip cursor={{
                fill: '#f9fafb'
              }} contentStyle={{
                borderRadius: '12px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} formatter={(value: number) => formatCurrency(value)} data-id="element-823" />
                <Bar dataKey="Diaspora" stackId="a" fill="#73C883" radius={[0, 0, 4, 4]} data-id="element-824" />
                <Bar dataKey="Bills" stackId="a" fill="#FFB347" data-id="element-825" />
                <Bar dataKey="Crowdfunding" stackId="a" fill="#3b82f6" data-id="element-826" />
                <Bar dataKey="Facility" stackId="a" fill="#8b5cf6" radius={[4, 4, 0, 0]} data-id="element-827" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex flex-col" data-id="element-828">
          <div className="flex items-center justify-between mb-6" data-id="element-829">
            <h3 className="text-lg font-semibold text-brand-text-dark" data-id="element-830">
              Recent Activity
            </h3>
            <button onClick={() => navigate('/dashboard/wallet')} className="text-sm font-medium text-brand-primary hover:text-brand-primary-dark" data-id="element-831">
              View All
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pr-2 space-y-4" data-id="element-832">
            {recentTransactions.map(tx => <div key={tx.id} className="flex items-center justify-between p-3 rounded-2xl hover:bg-gray-50 transition-colors" data-id="element-833">
                <div className="flex items-center gap-3" data-id="element-834">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${tx.amount > 0 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-835">
                    {tx.amount > 0 ? <ArrowDownRight size={20} data-id="element-836" /> : <ArrowUpRight size={20} data-id="element-837" />}
                  </div>
                  <div className="overflow-hidden" data-id="element-838">
                    <p className="text-sm font-semibold text-brand-text-dark truncate" data-id="element-839">
                      {tx.title}
                    </p>
                    <p className="text-xs text-brand-text-light" data-id="element-840">
                      {formatDate(tx.date)}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0" data-id="element-841">
                  <p className={`text-sm font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-brand-text-dark'}`} data-id="element-842">
                    {tx.amount > 0 ? '+' : ''}
                    {formatCurrency(tx.amount)}
                  </p>
                  <StatusBadge status={tx.status} data-id="element-843" />
                </div>
              </div>)}
          </div>
        </div>
      </div>

      {/* Add Funds Modal */}
      <Modal isOpen={isAddFundsOpen} onClose={() => setIsAddFundsOpen(false)} title="Add Funds to Wallet" data-id="element-844">
        <form onSubmit={handleAddFunds} className="space-y-4" data-id="element-845">
          <div data-id="element-846">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-847">
              Amount (₦)
            </label>
            <input type="number" value={fundAmount} onChange={e => setFundAmount(e.target.value)} placeholder="Enter amount" className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" min="1000" required data-id="element-848" />
          </div>

          {/* Mock Card Details */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3" data-id="element-849">
            <p className="text-sm font-medium text-gray-700" data-id="element-850">Payment Method</p>
            <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg" data-id="element-851">
              <div className="w-8 h-5 bg-blue-600 rounded flex items-center justify-center text-[8px] text-white font-bold" data-id="element-852">
                VISA
              </div>
              <div className="flex-1" data-id="element-853">
                <p className="text-sm font-medium" data-id="element-854">•••• •••• •••• 4242</p>
              </div>
            </div>
          </div>

          <button type="submit" className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3 rounded-xl font-semibold transition-colors mt-4" data-id="element-855">
            Fund Wallet
          </button>
        </form>
      </Modal>
    </div>;
};